// core
import { Injectable, Inject, ElementRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from "rxjs";
import { startWith } from 'rxjs/operators';
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { Observable, Subject } from 'rxjs';

// constants
import { RAPTOR_DEBOUNCE_TIME } from '../constant/raptor-constants';

// services
import { RaptorControlService } from './raptor-control.service';
import { RaptorDataService } from './raptor-data.service';
import { RaptorStepService } from './raptor-step.service';
import { RaptorAnalyticsService } from './raptor-analytics.service';
import { RaptorQuestionService } from './raptor-question.service';
import { RaptorStepDataService } from './raptor-step-data.service';
import { RaptorRepeaterService } from './raptor-repeater.service';
import { RaptorPaymentService } from './raptor-payment.service';

@Injectable({
  providedIn: 'root'
})
export class RaptorFormService {
  formData!: any;
  form!: FormGroup;
  formValueWatcher!: Subscription;
  step$: Observable<any>;
  private stepSubject: Subject<any>;
  update$: Observable<any>;
  private updateSubject: Subject<any>;

  constructor(
    private raptorControlService: RaptorControlService,
    private raptorDataService: RaptorDataService,
    private raptorStepService: RaptorStepService,
    private raptorAnalyticsService: RaptorAnalyticsService,
    private raptorQuestionService: RaptorQuestionService,
    private raptorStepDataService: RaptorStepDataService,
    private raptorRepeaterService: RaptorRepeaterService,
    private raptorPaymentService: RaptorPaymentService,
    @Inject('environment') public environment: any
  ) {
    this.stepSubject = new Subject<any>();
    this.step$ = this.stepSubject.asObservable();
    this.updateSubject = new Subject<any>();
    this.update$ = this.updateSubject.asObservable();
  }

  public setupStep(formData: any, step?: any) {
    this.formData = formData;
    // if a new step is being loaded then set it
    if (step) {
      this.formData = step;
    }

    this.raptorDataService.mapFormData(this.formData);
    this.raptorStepDataService.set(this.formData);
    this.form = this.raptorControlService.toFormGroup(this.formData);
    this.raptorAnalyticsService.startStep();
    this.raptorPaymentService.startStep();

    this.formWatcher(this.formData, this.form);
    
    this.stepSubject.next({
      formData: this.formData,
      form: this.form
    });
  }

  formWatcher(formData: any, form: FormGroup) {
    let changedField: any;

    if (this.formValueWatcher) {
      this.formValueWatcher.unsubscribe();
    }

    this.formValueWatcher = form.valueChanges
      .pipe(
        startWith(form.value),
        debounceTime(RAPTOR_DEBOUNCE_TIME),
        distinctUntilChanged((previous, current) => {
          changedField = this.raptorControlService.formValueChanged(previous, current);
          return !changedField;
        })
      )
      .subscribe(data => {
        let changedQuestion = this.raptorQuestionService.getQuestionByKey(formData, changedField);
        let questionSection = this.raptorQuestionService.getSectionByQuestionKey(formData, changedField);

        // update on change
        if (changedQuestion && changedQuestion.settings && changedQuestion.settings.updateOnChange) {
          if (changedQuestion.type === 'repeater') {
            let repeaterValue = form.getRawValue()[changedQuestion.key];
            if (
              repeaterValue &&
              (repeaterValue.indexOf('edit') < 0 || repeaterValue === 'edit_0') &&
              repeaterValue.indexOf('loaded') < 0 &&
              repeaterValue.indexOf('cancel') < 0
            ) {
              this.update(changedQuestion, questionSection, formData, form);
            }
          } else {
            this.update(changedQuestion, questionSection, formData, form);
          }
        }

        if (changedQuestion && changedQuestion.settings && changedQuestion.settings.validateOnChange) {
          this.raptorControlService.setQuestionValidators(form, form.getRawValue(), changedQuestion, questionSection);
        }

        if (changedField) {
          this.raptorPaymentService.calculatePayableFirstInCents(changedField, form.getRawValue());
        }
        
        if (
          changedQuestion &&
          changedQuestion.settings &&
          changedQuestion.settings.submitOnChange &&
          formData.settings?.hide_cta
        ) {
          // only submit when the submit button has been hidden
          this.submit(this.form, this.formData);
        }

        this.raptorControlService.applyQuestionFormatting(formData, form);
        this.raptorRepeaterService.repeatersValid(formData, form);

        if (this.raptorStepService.submitted) {
          this.raptorControlService.setValidators(formData, form, form.getRawValue());
        }

        if (changedField && changedQuestion) {
          this.trackQuestionChangeEvent(changedQuestion, data);
        }
      });
  }

  trackQuestionChangeEvent(question: any, formValues: any,): void {
    if (question && question.type !== 'repeater') {
      let questionValue = formValues[question.key];

      if (question.type === 'date' &&  questionValue && questionValue._isAMomentObject) {
        questionValue = {
          year: questionValue.year(),
          month: questionValue.month() + 1,
          day: questionValue.date()
        };
      }

      this.raptorAnalyticsService.trackEvent(
        'select-option',
        this.formData.flow_slug,
        question && question.uuid,
        this.formData.analytics,
        { value: questionValue }
      );

      // Send data to analytics service - question events only
      this.raptorAnalyticsService.questionChangeEvent(question, questionValue);
    }
  }

  async submit(form: FormGroup, formData: any) {
    this.raptorControlService.setValidators(formData, form, form.getRawValue());

    if (this.raptorPaymentService.paymentType) {
      let paymentField = this.raptorQuestionService.getQuestionByAttribute(this.raptorStepDataService.get(), 'type', 'payment');
      let paymentControls = form.get(paymentField.key);

      this.raptorControlService.setPaymentValidators(paymentControls, this.raptorPaymentService.paymentType);

      if (form.status !== 'VALID') {
        this.raptorPaymentService.displayError = true;
        this.raptorStepService.submitted = true;
        return;
      } else if (
        this.raptorPaymentService.paymentType === 'gocardless' &&
        this.raptorPaymentService.gocardlessShowAccount === false
      ) {
        if (form.status === 'VALID') {
          this.raptorPaymentService.gocardlessCreate(form)
            .then((result: any) => {
              // we want to use the `account_created` value in order to run predicate logic
              // in order to hide/show things in the UI
              let questionValue = paymentControls?.value;
              if (questionValue.gocardless) {
                questionValue.gocardless.account_created = true;
                let patchedValue: any = {};
                patchedValue[paymentField.key] = questionValue;
                form.patchValue(patchedValue, { emitEvent: true });
              }
            });
          return;
        }
      } else {
        // await payment Promise
        let paymentResult = await this.raptorPaymentService.pay(form);
        this.raptorPaymentService.paying = false;
        if (
          paymentResult &&
          (paymentResult.token || paymentResult.status === 'fulfilled')
        ) {
          this.raptorPaymentService.displayError = false;
          // attach the token to the form data
          paymentField.token = paymentResult.token;
          paymentField.account = this.raptorPaymentService.account;
        } else {
          this.raptorPaymentService.displayError = true;
          this.raptorStepService.submitted = true;
          return;
        }
      }
    }
    
    this.raptorStepService.next(formData, form)
      .then(step => {
        if (step) {
          const paymentType = this.raptorPaymentService.paymentType;
          if (paymentType) {
            this.raptorAnalyticsService.paymentEvent(step, formData, paymentType);
          }
          this.raptorAnalyticsService.finishStep(false, step);
          this.setupStep(step);
        } else {
          this.showFieldError();
        }
      })
      .catch(error => {
        this.showServerError(error);
      });
  }

  reset() {
    this.raptorStepService.back(this.formData)
      .then(step => {
        this.raptorAnalyticsService.finishStep(true);
        if (step) {
          this.setupStep(step);
        } 
      })
      .catch(error => {
        this.showServerError(error);
      });
  }

  async update(changedQuestion: any, section: any, formData: any, form: FormGroup): Promise<any> {
    let invalidForm = this.raptorControlService.checkFormPredicates(formData, form.getRawValue());
    if (invalidForm) {
      return new Promise((resolve) => {
        resolve(formData);
      });
    }
    
    if (!this.raptorStepService.loading && !this.raptorStepService.updating) {
      // set the validators on this field before calling update
      this.raptorControlService.setQuestionValidators(form, form.getRawValue(), changedQuestion, section);
      return this.raptorStepService.update(formData, form, changedQuestion)
        .then((response: any) => {
          if (
            response &&
            response.data &&
            response.data._flow_metadata &&
            (
              response.data._flow_metadata.pii_retries ||
              response.data._flow_metadata.pii_retries === false
            )
          ) {
            this.raptorAnalyticsService.finishStep(false);
            this.setupStep(response);
            return;
          }

          // update covers object first so that the extras question has the new covers values
          if (response && response.covers && formData.covers) {
            formData.covers = response.covers;
            this.raptorStepDataService.setCovers(response.covers);
          }
          // set the formData labels, infoboxes etc
          this.raptorDataService.setStepCopy(formData, response);
          this.raptorStepDataService.set(formData);
          
          let patchedValue: any = {};
          let changedValue = form.get(changedQuestion.key);
          patchedValue[changedQuestion.key] = changedValue?.value;
          form.patchValue(patchedValue, { emitEvent: true });
          // fire an event so that the medical component can detect the change
          this.updateSubject.next({
            formData: formData,
            form: form
          });
          return formData;
        })
        .catch(() => {
          this.raptorControlService.setValidators(formData, form, form.getRawValue());
          // set the form to be submitted to show field errors
          this.raptorStepService.submitted = true;
          this.showFieldError();
          return formData;
        });
    } else {
      return new Promise((resolve) => {
        resolve(formData);
      });
    }
  }

  edit(questionKey: string, formData: any) {
    this.raptorStepService.goToEdit(formData, questionKey)
      .then(step => {
        this.raptorAnalyticsService.finishStep(true);
        this.raptorStepService.clearQuestionToEdit();
        this.setupStep(step);
      })
      .catch(error => {
        this.showServerError(error);
      });
  }

  private showFieldError() {
    setTimeout(() => {
      let firstError: any =  document.querySelector('open-raptor-form form open-raptor-question .hasError');
      let position = firstError?.offsetTop;
      window.scrollTo({
        top: position,
        left: 0,
        behavior: 'smooth'
      });
    }, 0);
  }

  private showServerError(error: any) {
    if (error && error.status === 500) {
      // internal error
    }

    if (
      error && error.status === 400 &&
      error.error &&
      (error.error.progress !== null && error.error.progress > -1)
    ) {
      this.raptorStepService.resetForm('none');
      this.setupStep(error.error);
    }

    if (error && error.status === 401) {
      // unauthorised - user token invalid
    }

    if (error && error.status === 410) {
      // gone - quote already bound
    }

    if (error && typeof error.status === 'undefined' && !this.environment.production) {
      // TypeError or similar - potentially through bad flow config from Admin
      console.log(error);
    }
  }
}
